import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { LocationStrategy } from '@angular/common';
import { Component } from '@angular/core';
import { ApiRoutes } from '@sb/library/api';
import { BaseViewComponent, FormBase, number, PaymentContext, PaymentContextScope, PaymentProcessingScope, PaymentSourceMode, PaymentSourcesScope, Required, StripeRedirectStatus } from '@sb/library/base';
import { CalculateSurchargeInputItem, ErrorCode, InvoiceStatus, MakePaymentInputItem, PaymentCompleteDisplayItem, PaymentCompleteInputItem, PaymentDetailsDisplayItem, PaymentProcessorState, PaymentProcessorType, PaymentReturnStatus, PaymentSourceDisplayItem, PaymentSourceInputItem, PaymentSourceRegistrationNextAction, PaymentSourceSearchOptions, PaymentSourceType, PayPalCommercePlatformPaymentSourceInputItem, ProcessPaymentSourceResultItem, StripePaymentSourceInputItem, SurchargeResultItem, SurchargeType } from '@sb/library/data-models';
import { Subject } from 'rxjs';
import { PaymentErrorDialogComponent, PaymentErrorDialogScope } from '../dialogs/payment-error-dialog.component';
import { SBDateTime } from '@sb/library/localization';

class PaymentForm extends FormBase {
    Amount = Required(number);
}

const left = [
    query(':enter, :leave', style({ position: 'fixed', width: '100%' }), { optional: true }),
    group([
        query(':enter', [style({ transform: 'translateX(-100%)' }), animate('.5s ease-out', style({ transform: 'translateX(0%)' }))], {
            optional: true,
        }),
        query(':leave', [style({ transform: 'translateX(0%)' }), animate('.5s ease-out', style({ transform: 'translateX(100%)' }))], {
            optional: true,
        }),
    ]),
];

const right = [
    query(':enter, :leave', style({ position: 'fixed', width: '100%' }), { optional: true }),
    group([
        query(':enter', [style({ transform: 'translateX(100%)' }), animate('.5s ease-out', style({ transform: 'translateX(0%)' }))], {
            optional: true,
        }),
        query(':leave', [style({ transform: 'translateX(0%)' }), animate('.5s ease-out', style({ transform: 'translateX(-100%)' }))], {
            optional: true,
        }),
    ]),
];

@Component({
    selector: 'lib-payment-page',
    templateUrl: './payment-page.component.html',
    styleUrls: ['./payment-page.component.scss'],
    animations: [
        trigger('slideInOut', [
            transition(':increment', right),
            transition(':decrement', left),
        ]),
    ],
})
export class PaymentPageComponent extends BaseViewComponent implements PaymentProcessingScope, PaymentSourcesScope, PaymentErrorDialogScope {
    
    Scope = this.SetScope({
        Requires: PaymentContextScope,
        Implements: [PaymentProcessingScope, PaymentSourcesScope, PaymentErrorDialogScope]
    });

    readonly form = PaymentForm.Create(false);

    readonly triggerUpdateAmountWithSurcharge = new Subject<SurchargeResultItem>();
    readonly triggerProcessPaymentSource = new Subject<void>();
    readonly triggerProcessPaymentSourceNextStep = new Subject<ProcessPaymentSourceResultItem>();
    readonly triggerReenablePaymentProcessorForm = new Subject<void>();

    private _amountToPay: number | null = null;
    private _paymentSource!: PaymentSourceDisplayItem;
    private _processing: boolean = false;
    private _surcharge: number | null = null;
    private _totalAmountToPay: number | null = null;
    private _schoolHasCardFee: boolean = false;
    private _schoolHasBankFee: boolean = false;
    private _currentStep: number  = 0;
    private _hasAtLeastOnePaymentSource: boolean = false;
    private _existingPaymentMethodSelected: boolean = false;
    private _loadingPaymentReturnStatus: boolean = true;
    private _loadingPaymentSources: boolean = true;
    private _paymentProcessorReady: boolean = false;
    private _paymentSources: PaymentSourceDisplayItem[] = [];
    private _paymentSourceForNextStep: PaymentSourceInputItem | null = null;
    private _paymentCompleteDisplayItem!: PaymentCompleteDisplayItem;
    private _processPaymentSourceResultItem!: ProcessPaymentSourceResultItem;
    private _surchargeData: Array<SurchargeResultItem> = [];
    private _currentPaymentSourceType: PaymentSourceType = PaymentSourceType.CreditCard;
    
    constructor(
        private _LocationStrategy: LocationStrategy
    ) {
        super();
        this.CheckForPaymentReturn();
        history.pushState(null, document.title, window.location.href);
        this._LocationStrategy.onPopState(() => {
            if (this.context === PaymentContext.PaymentPortal) {
                history.go(1);
            }
        });
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (this.context === PaymentContext.SignupWidget || this.mode === PaymentSourceMode.Add) {
            throw Error('Payment page does not support either the signup widget context or Add mode');
        }
            
        this._amountToPay = this.Scope.paymentDetails.InvoiceAmountDue;
        
        if(this.Scope.schoolInfo.AllowPartialPayments && this._amountToPay){
            this.form.controls.Amount.setValue(this._amountToPay);
        }

        if (this.mode === PaymentSourceMode.Payment) {
            this.FetchPaymentSources();
        } else {
            this._loadingPaymentSources = false;
        }
    }

    PaymentProcessorReady(): void {
        this._processing = false;
        this._paymentProcessorReady = true;
    }

    ProcessPaymentSourceComplete(paymentSource: PaymentSourceInputItem) {
        ApiRoutes.PaymentSource.ProcessNew.Call({
            Parameter: this.Scope.mode === PaymentSourceMode.AnonymousPayment ? this.Scope.schoolInfo.ID : '',
            Body: paymentSource
        }).subscribe({
            next: result => {
                if (result.NextAction === PaymentSourceRegistrationNextAction.None) {
                    this._processPaymentSourceResultItem = result;
                    this.NextStep();
                } else {
                    this._paymentSourceForNextStep = paymentSource;
                    this.triggerProcessPaymentSourceNextStep.next(result);
                }
            },
            error: (error: typeof ApiRoutes.PaymentSource.ProcessNew.Error) => {
                if (error.Response?.ErrorCode === ErrorCode.OnlinePaymentError) {
                    this.CreateDialog(PaymentErrorDialogComponent, { IsProcessorError: true, ErrorMessage: error.Response.ErrorMessage! });
                } else {
                    throw error;
                }
            }
        });
    }

    PaymentErrorDismissed() {
        this._processing = false;
        this.triggerReenablePaymentProcessorForm.next();
    }

    PaymentSourceNextStepComplete(nextActionResult: string) {
        (this._paymentSourceForNextStep as StripePaymentSourceInputItem).IntentID = nextActionResult;
        this.ProcessPaymentSourceComplete(this._paymentSourceForNextStep!);
        this._paymentSourceForNextStep = null;
    }

    PaymentSourceSelected(paymentSource: PaymentSourceDisplayItem): void {
        this._paymentSource = paymentSource;
        this.ApplyOnlineConvenienceFee();
    }

    PaymentSourceTypeChanged(type: PaymentSourceType): void {
        this._currentPaymentSourceType = type;
        this.ApplyOnlineConvenienceFee();
    }

    ExistingPaymentMethodOpened(opened: boolean): void {
        if (opened) {
            this._existingPaymentMethodSelected = true;
            this.ApplyOnlineConvenienceFee();
        }
    }

    NewPaymentMethodOpened(opened: boolean): void {
        if (opened) {
            this._existingPaymentMethodSelected = false;
            this.ApplyOnlineConvenienceFee();
        }
    }

    AmountChanged(amount: number | null) {
        this._amountToPay = amount;
        this._surchargeData = [];
        this.ApplyOnlineConvenienceFee();
    }

    GetHintMessageForAmount(hasZeroAmountDue: boolean): string {
        return hasZeroAmountDue
            ? $localize`:@@CommonPaymentPageEnterAmountToPay:Enter an amount to pay`
            : $localize`:@@CommonPaymentPageFullAmountEnterPartialPayment:Pay full amount or enter a partial payment`;
    }

    PayUsingExistingPaymentMethod(): void {
        this._processing = true;
        ApiRoutes.PaymentSource.MakePayment.Call({
            Body: new MakePaymentInputItem({
                PaymentSourceID: this._paymentSource.ID,
                Amount: this._amountToPay! + (this.latePaymentFee ?? 0)
            })
        }).subscribe({
            next: data => {
                this._paymentCompleteDisplayItem = data;
                this._processing = false;
                this.NextStep();
            },
            error: data => this.CreateDialog(PaymentErrorDialogComponent, { IsProcessorError: true, ErrorMessage: data.Response.ErrorMessage })
        });
    }

    PayUsingNewPaymentMethod(): void {
        this._processing = true;
        this.triggerProcessPaymentSource.next();
    }

    DoneClicked(): void {
        this.Scope.Dismissed!();
    }

    private FetchPaymentSources(): void {
        ApiRoutes.PaymentSource.Search.Call({
            Body: new PaymentSourceSearchOptions({
                ParentIDs: [this.Scope.paymentDetails.ParentID!],
                Verified: true
            })
        }).subscribe({
            next: data => {
                this._paymentSources = data.ItemSubset;
                this._hasAtLeastOnePaymentSource = data.ItemSubset.length > 0 && this.Scope.paymentProcessor!.StoredPaymentMethodsSupported;
                this._loadingPaymentSources = false;
            },
            error: (error: typeof ApiRoutes.PaymentSource.Search.Error) => {
                this._loadingPaymentSources = false;
                if (error.Status === 401) {
                    this.Scope.FetchPaymentSourcesFailed?.();
                } else {
                    throw error;
                }
            }
        });
    }

    private CalculateOnlineConvenienceFee(): void {
        if (!this.amountToPay) {
            this._surcharge = null;
            this._totalAmountToPay = null;
        } else if (this._surchargeData.length) {
            this.SetOnlineConvenienceFee();
        } else {
            ApiRoutes.PaymentSource.CalculateSurcharge.Call({
                Parameter: this.schoolID,
                Body: new CalculateSurchargeInputItem({
                    Subtotal: this.amountToPay + (this.latePaymentFee ?? 0)
                })
            }).subscribe({
                next: result => {
                    this._surchargeData = result;
                    this.SetOnlineConvenienceFee();
                },
                error: (error: typeof ApiRoutes.PaymentSource.CalculateSurcharge.Error) => {
                    if (error.Status !== 401) {
                        throw error;
                    }
                }
            });
        }
    }

    private SetOnlineConvenienceFee(): void {
        const paymentSourceType = this._existingPaymentMethodSelected ? this._paymentSource.Type : this._currentPaymentSourceType;
        const surcharge = this._surchargeData.find(d => d.PaymentSourceType === paymentSourceType)!;
        this._totalAmountToPay = surcharge.Total;
        this._surcharge = surcharge.Surcharge;

        this.triggerUpdateAmountWithSurcharge.next(surcharge);
    }

    private NextStep(): void {
        this._currentStep = 1;
        this.Scope.PaymentSuccessful?.();
    }

    private ApplyOnlineConvenienceFee(): void {
        this.ResetConvenienceFees();
        const paymentSourceType = this._existingPaymentMethodSelected ? this._paymentSource.Type : this._currentPaymentSourceType;

        switch (paymentSourceType) {
            case PaymentSourceType.BankAccount:
            case PaymentSourceType.iDEAL:
                if (this.ChargesOnlineConvenienceFeeForBankAccounts()) {
                    this._schoolHasBankFee = true;
                    this.CalculateOnlineConvenienceFee();
                } else {
                    this.ResetConvenienceFees();
                }
                break;
            case PaymentSourceType.CreditCard:
            case PaymentSourceType.StripeLink:
            case PaymentSourceType.Klarna:
                if (this.ChargesOnlineConvenienceFeeForCreditCards()) {
                    this._schoolHasCardFee = true;
                    this.CalculateOnlineConvenienceFee();
                } else {
                    this.ResetConvenienceFees()
                }
                break;
        }
    }

    private ResetConvenienceFees(): void {
        this._schoolHasCardFee = false;
        this._schoolHasBankFee = false;
        this._surcharge = null;
        this._totalAmountToPay = (this._amountToPay ?? 0) + (this.latePaymentFee ?? 0);
        this.triggerUpdateAmountWithSurcharge.next(new SurchargeResultItem({ Total: this._totalAmountToPay, Surcharge: 0 }));
    }

    private ChargesOnlineConvenienceFeeForBankAccounts(): boolean {
        return this.Scope.schoolInfo.PaymentSurchargeACH! && this.Scope.schoolInfo.PaymentSurchargeACH.Type !== SurchargeType.None;
    }

    private ChargesOnlineConvenienceFeeForCreditCards(): boolean {
        return this.Scope.schoolInfo.PaymentSurchargeCreditCard! && this.Scope.schoolInfo.PaymentSurchargeCreditCard.Type !== SurchargeType.None;
    }

    private CheckForPaymentReturn(): void {
         //Check to see if we are coming back from PayPal or Stripe
        const queryParams = new URL(window.location.href).searchParams;
        const status = queryParams.get('SB_Status') as PaymentReturnStatus;
        const sptd = queryParams.get('SPTD');
        const token = queryParams.get('token');

        if (status) {
            let path = window.location.pathname;

            if (this.context === PaymentContext.PaymentPortal) {
                queryParams.delete('SB_Status');
                queryParams.delete('SPTD');
                queryParams.delete('token');

                path = `${path}?${queryParams}`;
            }

            history.replaceState(null, document.title, path);

            switch (status) {
                case PaymentReturnStatus.Cancelled:
                    this._loadingPaymentReturnStatus = false;
                    break;
                case PaymentReturnStatus.StripeCompleted:
                    const stripRedirectStatus = queryParams.get('redirect_status');
                    if (!stripRedirectStatus || stripRedirectStatus === StripeRedirectStatus.Success) { //if redirect status doesnt exists, or was a success. get details if we have them.
                        ApiRoutes.PaymentProcessor.GetDetails.Call({
                            Body: new PaymentCompleteInputItem({
                                SerializedTransactionDetails: sptd
                            })
                        }).subscribe(data => {
                            this._paymentCompleteDisplayItem = new PaymentCompleteDisplayItem({
                                Amount: data.PaidAmount,
                                PaymentProcessorTransactionID: queryParams.get('payment_intent'),
                                PaymentProcessorTimestamp: SBDateTime.Now().ToISO()
                            });

                            this._loadingPaymentReturnStatus = false;
                            this.NextStep();
                        });
                    } else if (stripRedirectStatus === StripeRedirectStatus.Failure) {
                        this._loadingPaymentReturnStatus = false; //Cancelled stage
                    }
                    break;
                case PaymentReturnStatus.PayPalCompleted:
                    const intentAutoPayCode = queryParams.get('I');
                    if (!token || !sptd || !intentAutoPayCode) {
                        console.error('Missing data; cannot complete order (PayPal Commerce Platform)');
                    } else {
                        this.ProcessPayPalCommercePlatformOrderCapture(token, intentAutoPayCode, sptd);
                    }
                    break;
            }
        } else {
            this._loadingPaymentReturnStatus = false;
        }
    }

    private ProcessPayPalCommercePlatformOrderCapture(orderID: string, intentAutoPayCode: string, transactionDetails: string) {
        const inputItem = new PayPalCommercePlatformPaymentSourceInputItem({
            SerializedTransactionDetails: transactionDetails,
            OrderID: orderID,
            IntentAutoPayCode: intentAutoPayCode
        });

        ApiRoutes.PaymentSource.ProcessNew.Call({
            Parameter: this.mode === PaymentSourceMode.AnonymousPayment ? this.schoolID : '',
            Body: inputItem
        }).subscribe({
            next: result => {
                this._processPaymentSourceResultItem = result;
                this._totalAmountToPay = result.AmountPaid;
                this._loadingPaymentReturnStatus = false;

                this.NextStep();
            },
            error: (error: typeof ApiRoutes.PaymentSource.ProcessNew.Error) => {
                if (error.Response?.ErrorCode === ErrorCode.OnlinePaymentError) {
                    this.CreateDialog(PaymentErrorDialogComponent, { IsProcessorError: true, ErrorMessage: error.Response.ErrorMessage! });
                } else {
                    throw error;
                }
            }
        });
    }

    get processing(): boolean {
        return this._processing;
    }

    get context(): PaymentContext {
        return this.Scope.context;
    }

    get mode(): PaymentSourceMode {
        return this.Scope.mode;
    }

    get forceAutoPay(): boolean {
        return this.Scope.schoolInfo.ForceAutoPay;
    }

    get schoolHasSurcharge(): boolean {
        return ((this._schoolHasBankFee && this.Scope.paymentProcessor!.Type === PaymentProcessorType.Stripe) || this._schoolHasCardFee);
    }

    get invoiceStatus(): InvoiceStatus {
        return this.Scope.paymentDetails.InvoiceStatus;
    }

    get surcharge(): number | null {
        return this._surcharge;
    }

    get surchargeReady(): boolean {
        return !(this.schoolHasSurcharge && !this._surcharge);
    }

    get currentStep(): number {
        return this._currentStep;
    }

    get hasAtLeastOnePaymentSource(): boolean {
        return this._hasAtLeastOnePaymentSource;
    }

    get loadingPaymentReturnStatus(): boolean {
        return this._loadingPaymentReturnStatus;
    }

    get loadingPaymentSources(): boolean {
        return this._loadingPaymentSources;
    }

    get paymentProcessorReady(): boolean {
        return this._paymentProcessorReady;
    }

    get loading(): boolean {
        return this.loadingPaymentReturnStatus || this.loadingPaymentSources;
    }

    get paymentSources(): PaymentSourceDisplayItem[] {
        return this._paymentSources;
    }

    get amountToPay(): number | null {
        return this._amountToPay;
    }

    get totalAmountToPay(): number | null {
        return this._totalAmountToPay;
    }

    get paymentCompleteDisplayItem(): PaymentCompleteDisplayItem {
        return this._paymentCompleteDisplayItem;
    }

    get processPaymentSourceResultItem(): ProcessPaymentSourceResultItem {
        return this._processPaymentSourceResultItem;
    }

    get latePaymentFee(): number | null {
        return this.Scope.paymentDetails.LateFee;
    }

    get schoolName(): string {
        return this.Scope.schoolInfo.Name!;
    }

    get schoolID(): string {
        return this.Scope.schoolInfo.ID;
    }

    get hasZeroAmountDue(): boolean {
        return !this.Scope.paymentDetails.InvoiceAmountDue;
    }

    get SurchargeType(): typeof SurchargeType {
        return SurchargeType;
    }

    get InvoiceStatus(): typeof InvoiceStatus {
        return InvoiceStatus;
    }

    get PaymentProcessorType(): typeof PaymentProcessorType {
        return PaymentProcessorType;
    }

    get PaymentContext(): typeof PaymentContext {
        return PaymentContext;
    }

    get PaymentProcessorState(): typeof PaymentProcessorState {
        return PaymentProcessorState;
    }

    get PaymentSourceMode(): typeof PaymentSourceMode {
        return PaymentSourceMode;
    }
}
